import { useSelector } from "react-redux";

import { MODULE_NOT_ENABLED_MESSAGE, MODULE_STATE, MODULE_UNAVAILABLE_MESSAGE, getModuleState } from "@/permissions/modules";

/**
 * Resolve one module to its rendering state for the signed-in user.
 *
 * Use this for feature surfaces that are NOT sidebar items and own no
 * permission code of their own — the interview-questions panel, the boolean
 * search tab, the job-fit button. Sidebar items and routes are already handled
 * by permission filtering plus `PermissionGuard`, which need no change.
 *
 * Returns `{ state, isEnabled, isAvailable, canUse, message }`:
 *
 * - `isEnabled`  — in the tenant's plan. False → hide the surface entirely.
 * - `isAvailable`— operational fleet-wide. False → keep the surface VISIBLE
 *                  with existing data rendered, and disable only the action.
 * - `canUse`     — both. What an action control should key off.
 * - `message`    — the right copy for the state, or null when usable.
 *
 * The two flags are separate on purpose: "you never bought this" and "this is
 * temporarily down" must not look the same to the user. Presentation only —
 * the backend re-reads module state on every request, so there is no window in
 * which a disabled module is actually usable.
 *
 * @param {string} moduleCode  a value from MODULES
 */
export const useModuleAccess = (moduleCode) => {
  const user = useSelector((state) => state.auth?.user);

  const state = getModuleState(moduleCode, user);
  const isEnabled = state !== MODULE_STATE.NOT_ENTITLED;
  const isAvailable = state === MODULE_STATE.AVAILABLE;

  let message = null;
  if (state === MODULE_STATE.NOT_ENTITLED) message = MODULE_NOT_ENABLED_MESSAGE;
  else if (state === MODULE_STATE.UNAVAILABLE) message = MODULE_UNAVAILABLE_MESSAGE;

  return {
    state,
    isEnabled,
    isAvailable,
    canUse: isEnabled && isAvailable,
    message,
  };
};

/** Convenience alias for the common case of only needing the kill-switch state. */
export const useModuleAvailability = (moduleCode) => {
  const { isAvailable, message } = useModuleAccess(moduleCode);
  return { isAvailable, message };
};

export default useModuleAccess;
